import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import TableCompo from "../CommonTable/TableCompo";
import { getPassbookDataRequest } from "../../redux/Passbook/passbookSlice";
import useCurrency from "../../hooks/useCurrency";
import { useAppTheme } from "../../theme/useAppTheme";

const PassbookScreen = () => {
  const dispatch = useDispatch();

  const {
    data: passbook,
    loading,
    error,
  } = useSelector((state) => state.passbook || {});
  const { appConfig } = useSelector((state) => state.dashboard || {});
  const decimal = appConfig?.decimalPrecision ?? 4;
  const [filterType, setFilterType] = useState("All");
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const { symbol } = useCurrency();
  const themeColors = useAppTheme();

  useEffect(() => {
    dispatch(getPassbookDataRequest());
  }, [dispatch]);

  const filteredPassbook = useMemo(() => {
    if (!Array.isArray(passbook)) return [];
    if (filterType === "All") return passbook;
    return passbook.filter(
      (item) => item.order_type?.toLowerCase().includes(filterType.toLowerCase())
    );
  }, [passbook, filterType]);

  const columns = [
    { field: "metal", headerName: "Metal", flex: 1 },
    { field: "date", headerName: "Date & Time", flex: 1.5 },
    { field: "weight", headerName: "Weight (gms)", flex: 1 },
    { field: "transactionType", headerName: "Transaction Type", flex: 1 },
    { field: "amount", headerName: "Amount", flex: 1 },
  ];

  const tableData = filteredPassbook.map((item, index) => ({
    id: index + 1,
    metal: item?.metalName || "N/A",
    date: item?.createdAt
      ? new Date(item.createdAt).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "N/A",
    weight: item?.weight || "N/A",
    transactionType: item?.order_type || "N/A",
    amount: item?.amount ? `${symbol}${Number(item.amount).toFixed(decimal)}` : `${symbol}0.00`,
  }));
  const handleTableChange = (type, payload) => {
  if (type === "changePage") {
    setPage(payload.page);
  }

  if (type === "changeRowsPerPage") {
    setLimit(payload.rowsPerPage);
    setPage(0);
  }
  };
  const paginatedData = tableData.slice(
  page * limit,
  page * limit + limit
  );
  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 1300,
        mx: "auto",
        mt: 8,
        px: { xs: 1.5, sm: 2 },
        color: themeColors.scaffoldText,
      }}
    >
      <Box sx={{ mb: 2 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: themeColors.primary,
                fontFamily: "Aeonik",
                mb: 1.5,
              }}
            >
              Passbook
            </Typography>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1, mb: 2 }}>
              <FormControl size="small" 
                sx={{
                  minWidth: 200,
                  "& .MuiSelect-select": { color: themeColors.primary },
                }}
              >
                <InputLabel>Filter</InputLabel>
                <Select
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    setPage(0);
                  }
                  }
                  label="Filter"
                  MenuProps={{
                    PaperProps: {
                      sx: { background: themeColors.mediumContainer, color: themeColors.text },
                    },
                  }}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="Buy">Buy</MenuItem>
                  <MenuItem value="Sell">Sell</MenuItem>
                  <MenuItem value="Gift">Gift</MenuItem>
                  <MenuItem value="Redeem">Redeem</MenuItem>
                  <MenuItem value="Sip">SIP</MenuItem>
                  <MenuItem value="Installment">Installment</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ textAlign: "center", mt: 10 }}>
              <CircularProgress color="primary" />
              <Typography variant="body2" mt={2}>
                Loading passbook...
              </Typography>
            </Box>
          ) : error ? (
            <Typography color="error" textAlign="center" mt={5}>
              {error}
            </Typography>
          ) : (
            <Box
              sx={{
                height: "70vh",
                width: "100%",
                minWidth: 0,
                overflow: "auto",
              }}
            >
              <TableCompo
                title="Transactions"
                columns={columns}
                data={paginatedData}
                page={page}
                limit={limit}
                totalCount={tableData.length}
                onTableChange={handleTableChange}
                loading={loading}
              />
            </Box>
          )}
    </Box>
  );
};

export default PassbookScreen;
